import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Search } from 'lucide-react'
import Masonry from 'react-masonry-css'
import { useMobileColumns } from '../../hooks/useMobileColumns'
import { useCurationMemberships } from '../../hooks/useCurationMemberships'
import { AddProduct } from './AddProduct'
import { ItemCard } from './ItemCard'
import { ItemCardSkeleton } from './ItemCardSkeleton'
import { EmptyState } from './EmptyState'
import { ProfileHeader } from '../profile/ProfileHeader'
import { ProfileMasonry } from './ProfileMasonry'
import { BrandsDirectory } from '../brands/BrandsDirectory'
import { CuratedIssues } from '../issues/CuratedIssues'
import { CurationsGrid } from '../profile/CurationsGrid'
import { NewCurationModal } from '../profile/NewCurationModal'
import { EditProfileModal } from '../profile/EditProfileModal'
import { contributionsService } from '../../lib/contributions'
import { curationsService, Curation } from '../../lib/curations'
import { supabase, ensureValidSession } from '../../lib/supabase'
import { adminService } from '../../lib/admin'
import { useContributions } from '../../contexts/ContributionsContext'
import { AnimatePresence } from 'framer-motion'
import { FeedCommentsOverlay } from './FeedCommentsOverlay'

interface ContributionPlatformProps {
  user: any
}

export function ContributionPlatform({ user }: ContributionPlatformProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const currentView = location.state?.view || 'discover'
  const context = useOutletContext<any>()
  const activeFilters = context?.activeFilters || null
  const mobileColumns = useMobileColumns()
  const { cache, isLoading: contextLoading, isFetchingMore, error: contextError, fetchContributions: fetchCachedContributions, fetchNextPage, checkForUpdates, toggleSave, updateContribution } = useContributions()
  const [profileViewMode, setProfileViewMode] = useState<'shared' | 'saved' | 'issues'>('shared')
  const [isAdmin, setIsAdmin] = useState(false)
  const [userProfile, setUserProfile] = useState<any>(null)
  const [localLoading, setLocalLoading] = useState(false)
  const [userCurations, setUserCurations] = useState<Curation[]>([])
  const [showProfileCompletionModal, setShowProfileCompletionModal] = useState(false)
  const [showNewCurationModal, setShowNewCurationModal] = useState(false)
  const [activeCommentsCard, setActiveCommentsCard] = useState<string | null>(null) // Track which card has comments open
  const [panelSide, setPanelSide] = useState<'left' | 'right'>('right')
  const [activeCardRect, setActiveCardRect] = useState<{ top: number; bottom: number; left: number; right: number } | undefined>(undefined)
  const [isLargeScreen, setIsLargeScreen] = useState(typeof window !== 'undefined' ? window.innerWidth >= 1024 : false)
  const hasCheckedForUpdatesRef = useRef(false) // Track if we've checked for updates this session
  const loadMoreRef = useRef<HTMLDivElement>(null) // Ref for infinite scroll trigger

  // Track screen size for flyover vs inline comments
  useEffect(() => {
    const handler = () => setIsLargeScreen(window.innerWidth >= 1024)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  // Handle opening comments with panel side computation
  const handleCommentsOpen = useCallback((contributionId: string) => {
    const cardEl = document.querySelector(`[data-card-id="${contributionId}"]`)
    if (cardEl) {
      const rect = cardEl.getBoundingClientRect()
      const cardCenter = rect.left + rect.width / 2
      setPanelSide(cardCenter < window.innerWidth / 2 ? 'right' : 'left')
      setActiveCardRect({ top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right })
    }
    setActiveCommentsCard(contributionId)
  }, [])

  // State for filtered backend results when frontend cache has no matches
  const [isLoadingFilteredResults, setIsLoadingFilteredResults] = useState(false)
  const [backendFilteredContributions, setBackendFilteredContributions] = useState<any[] | null>(null)
  const lastFilterRef = useRef<string>('') // Track last filter to avoid duplicate queries

  // Use cached data
  const contributions = cache.contributions
  const userContributions = cache.userContributions
  const savedContributions = cache.savedContributions
  const isLoading = contextLoading || localLoading
  const error = contextError

  // Fetch contributions on mount
  useEffect(() => {
    // Ensure valid session on mount
    ensureValidSession().then((session) => {
      console.log('Session validated on mount:', !!session);
    });

    // Check if user is admin
    adminService.checkAdminStatus(user.id).then(status => {
      setIsAdmin(status);
    });
    
    // Ensure user profile exists before fetching contributions
    ensureUserProfile().then(() => {
      // Fetch contributions using cache (will use cached data if available)
      // This will automatically fetch if cache is empty
      fetchCachedContributions(user.id);
      
      // Fetch curated issues
      fetchUserCurations(user.id);
    });
    
    // Real-time subscription disabled to prevent items from jumping while scrolling
    // Users can manually refresh or navigate away/back to see new content

    // const subscription = contributionsService.subscribeToContributions((payload) => {
    //   console.log('Contribution update:', payload);
    //   if (payload.eventType === 'INSERT') {
    //     // Force refresh on new contributions
    //     fetchCachedContributions(user.id, true);
    //   }
    // });

    // return () => {
    //   subscription.unsubscribe();
    // };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  // Check for updates when switching to community view (only once per session)
  useEffect(() => {
    const checkAndRefresh = async () => {
      if (currentView === 'discover' && !contextLoading && !hasCheckedForUpdatesRef.current) {
        // Mark that we've checked for updates this session
        hasCheckedForUpdatesRef.current = true;

        // If cache is empty, always fetch
        if (contributions.length === 0) {
          console.log('Community view with empty cache, fetching...');
          fetchCachedContributions(user.id);
          return;
        }

        // Check if new content exists
        console.log('Checking for new contributions...');
        const hasUpdates = await checkForUpdates();
        if (hasUpdates) {
          console.log('New content detected, refreshing feed...');
          fetchCachedContributions(user.id, true); // Force refresh
        } else {
          console.log('No new content, using cache');
        }
      }
    };

    checkAndRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView])

  // Infinite scroll - load more when user reaches bottom
  useEffect(() => {
    if (!loadMoreRef.current || currentView !== 'discover') return

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0]
        if (firstEntry.isIntersecting && cache.hasMore && !isFetchingMore && !contextLoading) {
          console.log('Load more trigger visible, fetching next page...')
          fetchNextPage(user.id)
        }
      },
      {
        rootMargin: '400px' // Start loading before user reaches the bottom
      }
    )

    observer.observe(loadMoreRef.current)

    return () => observer.disconnect()
  }, [currentView, cache.hasMore, isFetchingMore, contextLoading, fetchNextPage, user.id])

  // Always query the database when search filters are active
  // The frontend cache is lazily loaded and incomplete — search must hit the DB
  useEffect(() => {
    const fetchFilteredFromBackend = async () => {
      if (!activeFilters) {
        setBackendFilteredContributions(null)
        lastFilterRef.current = ''
        return
      }

      const hasAnyFilter = activeFilters.query ||
        (activeFilters.tags && activeFilters.tags.length > 0) ||
        (activeFilters.categories && activeFilters.categories.length > 0) ||
        (activeFilters.subcategories && activeFilters.subcategories.length > 0) ||
        (activeFilters.brands && activeFilters.brands.length > 0)

      if (!hasAnyFilter) {
        setBackendFilteredContributions(null)
        lastFilterRef.current = ''
        return
      }

      const filterKey = JSON.stringify(activeFilters)
      if (filterKey === lastFilterRef.current) {
        return
      }

      lastFilterRef.current = filterKey
      setIsLoadingFilteredResults(true)

      try {
        const hasCategories = activeFilters.categories && activeFilters.categories.length > 0
        const hasSubcategories = activeFilters.subcategories && activeFilters.subcategories.length > 0
        const hasTags = activeFilters.tags && activeFilters.tags.length > 0

        // When filtering by category/subcategory, use !inner join so the
        // DB filters matched rows *before* the limit is applied.
        const classificationJoin = (hasCategories || hasSubcategories)
          ? 'item_classifications!inner'
          : 'item_classifications'

        let query = supabase
          .from('contributions')
          .select(`
            *,
            user:users!contributions_user_id_fkey(id, first_name, last_name, avatar_url, username),
            ${classificationJoin}(
              category_id,
              subcategory_id,
              categories(id, name),
              subcategories(id, name)
            ),
            item_tags(
              tag_id,
              tags(id, name)
            )
          `)
          .eq('is_active', true)
          .eq('is_hidden', false)
          .order('created_at', { ascending: false })
          .limit(100)

        // Split search into individual terms so "footwear sneakers" matches
        // items where each word appears in any searchable field
        if (activeFilters.query) {
          const terms = activeFilters.query.split(/\s+/).filter(Boolean)
          for (const term of terms) {
            const searchTerm = `%${term}%`
            query = query.or(`title.ilike.${searchTerm},brand.ilike.${searchTerm},description.ilike.${searchTerm}`)
          }
        }

        if (activeFilters.brands && activeFilters.brands.length > 0) {
          query = query.in('brand', activeFilters.brands)
        }

        // Push category/subcategory filters into the DB query
        if (hasCategories) {
          query = query.in('item_classifications.category_id', activeFilters.categories)
        }
        if (hasSubcategories) {
          query = query.in('item_classifications.subcategory_id', activeFilters.subcategories)
        }

        const { data, error } = await query

        if (error) {
          console.error('Error fetching search results:', error)
          setBackendFilteredContributions([])
          setIsLoadingFilteredResults(false)
          return
        }

        let results = data || []

        // Tags use names (not IDs), so filter client-side
        if (hasTags) {
          results = results.filter(item => {
            const itemTags = item.item_tags || []
            return activeFilters.tags.some((tag: string) =>
              itemTags.some((it: any) =>
                it.tags?.name?.toLowerCase() === tag.toLowerCase()
              )
            )
          })
        }

        // Batch check which items the user has saved
        const resultIds = results.map((r: any) => r.id).filter(Boolean)
        let savedIds: Set<string> = new Set()
        if (resultIds.length > 0) {
          savedIds = await curationsService.checkUserSaves(user.id, resultIds)
        }

        const transformedResults = results.map((item: any) => {
          const classifications = item.item_classifications || []
          const primaryClassification = classifications[0]
          const tags = item.item_tags || []

          return {
            ...item,
            category_id: primaryClassification?.category_id || null,
            category_name: primaryClassification?.categories?.name || null,
            subcategory_id: primaryClassification?.subcategory_id || null,
            subcategory_name: primaryClassification?.subcategories?.name || null,
            tag_names: tags.map((t: any) => t.tags?.name).filter(Boolean),
            is_saved: savedIds.has(item.id),
            saves_count: item.saves_count || 0
          }
        })

        setBackendFilteredContributions(transformedResults)
      } catch (err) {
        console.error('Error fetching search results:', err)
        setBackendFilteredContributions([])
      } finally {
        setIsLoadingFilteredResults(false)
      }
    }

    fetchFilteredFromBackend()
  }, [activeFilters, user.id])


  const fetchUserCurations = async (userId: string) => {
    try {
      const curations = await curationsService.getUserCurations(userId)

      // Get item counts for each curation
      const curationsWithCounts = await Promise.all(
        curations.map(async (curation) => {
          const items = await curationsService.getCurationItems(curation.id)
          return {
            ...curation,
            item_count: items.length
          }
        })
      )

      setUserCurations(curationsWithCounts)
    } catch (error) {
      console.error('Error fetching user curations:', error)
      setUserCurations([])
    }
  }

  const ensureUserProfile = async () => {
    try {
      // Fetch the user profile
      const { data: profile } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .maybeSingle()
      
      // If profile doesn't exist, create it (shouldn't happen due to trigger)
      if (!profile) {
        // Profile doesn't exist, create it (shouldn't happen due to trigger)
        console.log('No profile found, creating user profile for:', user.id)
        
        const profileData = {
          id: user.id,
          email: user.email,
          first_name: user.user_metadata?.first_name || user.email?.split('@')[0] || '',  // Use email prefix as fallback
          last_name: user.user_metadata?.last_name || '',   // Leave empty if not provided
          avatar_url: user.user_metadata?.avatar_url || '',
          username: user.user_metadata?.username || user.email?.split('@')[0] || '',
          interests: [],
          bio: '',
          location: '',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          is_verified: false,
          email_notifications: true
        }
        
        console.log('Inserting profile data:', profileData)
        
        const { data: newProfile, error: insertError } = await supabase
          .from('users')
          .insert(profileData)
          .select()
          .single()
        
        if (insertError) {
          console.error('Error creating user profile:', insertError)
          console.error('Insert error details:', insertError.details, insertError.hint, insertError.message)
          
          // If insert failed due to duplicate, try to fetch the existing profile
          if (insertError.code === '23505') { // Duplicate key violation
            console.log('Profile already exists (duplicate key), fetching it...')
            const { data: existingProfile } = await supabase
              .from('users')
              .select('*')
              .eq('id', user.id)
              .single()
            
            if (existingProfile) {
              console.log('Fetched existing profile:', existingProfile)
              setUserProfile(existingProfile)
            }
          }
        } else {
          console.log('User profile created successfully:', newProfile)
          setUserProfile(newProfile)
        }
      } else {
        // Profile exists
        setUserProfile(profile);
        
        // Check if profile needs to be completed
        if (!profile.profile_completed) {
          console.log('Profile not completed, showing mandatory modal');
          setShowProfileCompletionModal(true);
        }
      }
    } catch (err) {
      console.error('Error ensuring user profile:', err)
    }
  }



  const handleAddContribution = async (formData: any) => {
    try {
      setLocalLoading(true)
      
      // Map the form data to match our database schema
      const contribution = {
        title: formData.title,
        brand: formData.brand,
        category: formData.category,
        description: formData.description,
        image_url: formData.image_url || formData.image || null, // Handle both field names
        images: formData.images || [], // Include the multiple images array
        source_url: formData.source_url || formData.url || null, // Include the product source link
        impact: formData.impact || null,
        tags: formData.tags || [],
        comment: formData.comment || null, // User's personal comment about why they love it
        price_amount: formData.price_amount ?? null,
        price_currency: formData.price_currency ?? null,
        // Pass through classification data
        _classification: formData._classification,
        _skipAutoClassify: formData._skipAutoClassify,
        _selectedCategoryId: formData._selectedCategoryId,
        _selectedSubcategoryId: formData._selectedSubcategoryId
      }
      
      console.log('Creating contribution:', contribution) // Debug log
      console.log('About to call createContribution...')
      console.log('User ID:', user.id)
      
      // Ensure we have a fresh session before creating contribution
      console.log('Refreshing session before contribution creation...')
      const freshSession = await ensureValidSession()
      
      if (!freshSession || !freshSession.access_token) {
        throw new Error('No valid session available - please log in again')
      }
      
      // Session validated and ready
      
      const result = await contributionsService.createContribution(contribution, user.id, freshSession.access_token)
      console.log('Contribution created result:', result)
      
      // Force a cache refresh to get the properly formatted data with all joins
      console.log('Refreshing cache to get complete contribution data...')
      await fetchCachedContributions(user.id, true) // Force refresh
      
      // Switch back to community view
      navigate('/', { state: { view: 'discover' } })
      console.log('Switched to community view')
      
      // Return successfully to indicate completion
      return result
    } catch (err: any) {
      console.error('Error creating contribution:', err)
      console.error('Error stack:', err.stack)
      
      // Log more details about the error
      if (err.code) console.error('Error code:', err.code)
      if (err.details) console.error('Error details:', err.details)
      if (err.hint) console.error('Error hint:', err.hint)
      
      // Re-throw the error so the form knows it failed
      throw err
    } finally {
      setLocalLoading(false)
    }
  }

  const handleSaveToggle = async (contributionId: string, isSaved: boolean) => {
    try {
      // Use the context's toggleSave function which handles caching
      await toggleSave(contributionId, user.id, isSaved)
    } catch (err) {
      console.error('Error toggling save:', err)
    }
  }
  

  // When search is active, always use database results — not the partial cache
  const filteredContributions = useMemo(() => {
    return activeFilters ? (backendFilteredContributions || []) : contributions
  }, [activeFilters, backendFilteredContributions, contributions])

  const hasContributions = contributions.length > 0

  // Get item IDs for batch curation membership lookup
  const displayedItemIds = useMemo(() => {
    return filteredContributions.map(item => item.id).filter(Boolean) as string[]
  }, [filteredContributions])

  // Batch fetch curation memberships for all displayed items (2 queries total instead of N*2)
  const {
    userCurations: batchedUserCurations,
    toggleCuration,
    createCurationWithItem,
    getMembershipsForItem
  } = useCurationMemberships({
    currentUserId: user.id,
    itemIds: displayedItemIds
  })

  // Stats for header (for future use)
  // const stats = {
  //   totalContributions: contributions.length,
  //   myContributions: userContributions.length,
  //   totalSaves: userContributions.reduce((sum, c) => sum + (c.saves_count || 0), 0)
  // }


  return (
    <>
      <main
        className={
          currentView === 'curations' || currentView === 'discover' ? '' : 'py-6'
        }
      >
        {error && (
          <div className="container mx-auto px-4 mb-6">
            <div className="p-4 bg-destructive/10 text-destructive rounded-lg">
              {error}
            </div>
          </div>
        )}

        {currentView === 'contribute' ? (
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <AddProduct onSubmit={handleAddContribution} currentUserId={user.id} />
            </motion.div>
          </div>
        ) : currentView === 'curations' ? (
          <CuratedIssues />
        ) : currentView === 'discover' ? (
          <>
            <div className="bg-[#57241F] text-white mb-6">
              <div className="container mx-auto px-4 py-6 md:py-8 max-w-4xl text-center">
                <p
                  className="text-sm md:text-base uppercase tracking-widest opacity-90"
                  style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                >
                  Shared by
                </p>
                <h2
                  className="text-2xl md:text-3xl mt-2"
                  style={{ fontFamily: 'Canela, Georgia, serif', fontWeight: 300 }}
                >
                  The Community
                </h2>
              </div>
            </div>

            {isLoading && !hasContributions ? (
              // Show skeleton cards while loading
              <Masonry
                breakpointCols={{
                  default: 4,
                  1280: 4,
                  1024: 3,
                  768: 2,
                  640: mobileColumns === 2 ? 2 : 1
                }}
                className="flex gap-3 px-3 md:px-0"
                columnClassName="bg-clip-padding"
              >
                {Array.from({ length: 12 }).map((_, index) => (
                  <div key={`skeleton-${index}`} className="mb-3">
                    <ItemCardSkeleton />
                  </div>
                ))}
              </Masonry>
            ) : !hasContributions ? (
              <div className="container mx-auto px-4">
                <EmptyState onStartContributing={() => navigate('/', { state: { view: 'contribute' } })} />
              </div>
            ) : isLoadingFilteredResults ? (
              // Show skeleton cards while loading filtered results from backend
              <div>
                <div className="container mx-auto px-4 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full" />
                    <span>Searching for matching items...</span>
                  </div>
                </div>
                <Masonry
                  breakpointCols={{
                    default: 4,
                    1280: 4,
                    1024: 3,
                    768: 2,
                    640: mobileColumns === 2 ? 2 : 1
                  }}
                  className="flex gap-3 px-3 md:px-0"
                  columnClassName="bg-clip-padding"
                >
                  {Array.from({ length: 8 }).map((_, index) => (
                    <div key={`filter-skeleton-${index}`} className="mb-3">
                      <ItemCardSkeleton />
                    </div>
                  ))}
                </Masonry>
              </div>
            ) : filteredContributions.length === 0 ? (
              <div className="container mx-auto px-4">
                <div className="text-center py-16">
                  <Search className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No results found</p>
                  <p className="text-xs text-gray-400 mt-1">Try different keywords</p>
                </div>
              </div>
            ) : (
              <Masonry
                breakpointCols={{
                  default: 4,
                  1280: 4,
                  1024: 3,
                  768: 2,
                  640: mobileColumns === 2 ? 2 : 1
                }}
                className="flex gap-3 px-3 md:px-0"
                columnClassName="bg-clip-padding"
              >
                {filteredContributions.map((item, index) => (
                  <div key={item.id} className="mb-3" data-card-id={item.id}>
                    <ItemCard
                      item={{
                        ...item,
                        image: item.image_url,
                        cached_image: item.cached_image_url,
                        dateAdded: item.created_at,
                        isSaved: item.is_saved,
                        source_url: item.source_url,
                        saves_count: item.saves_count || 0,
                        user: item.user
                      }}
                      onSaveToggle={(saved) => handleSaveToggle(item.id!, item.is_saved || false)}
                      onClick={undefined}
                      currentUserId={user.id}
                      currentUser={userProfile ? {
                        avatar_url: userProfile.avatar_url,
                        first_name: userProfile.first_name
                      } : undefined}
                      // Batch-loaded curation data (avoids N+1 queries)
                      userCurations={batchedUserCurations}
                      curationsWithItem={getMembershipsForItem(item.id)}
                      onToggleCuration={(curationId) => toggleCuration(item.id, curationId)}
                      onCreateCuration={(title) => createCurationWithItem(title, item.id)}
                      // Comments coordination (only one card can have comments open at a time)
                      isCommentsOpen={activeCommentsCard === item.id}
                      onCommentsOpen={handleCommentsOpen}
                      onCommentsClose={() => setActiveCommentsCard(null)}
                    />
                  </div>
                ))}
                {/* Show skeleton placeholders while loading more */}
                {isFetchingMore && Array.from({ length: 8 }).map((_, index) => (
                  <div key={`loading-skeleton-${index}`} className="mb-3">
                    <ItemCardSkeleton />
                  </div>
                ))}
              </Masonry>
            )}

            {/* Infinite Scroll Trigger */}
            {currentView === 'discover' && !error && (
              <div ref={loadMoreRef} className="w-full py-4" />
            )}

            {/* Feed Comments Flyover (desktop >= 1024px) */}
            <AnimatePresence>
              {isLargeScreen && activeCommentsCard && (() => {
                const activeItem = filteredContributions.find(c => c.id === activeCommentsCard)
                if (!activeItem) return null
                return (
                  <FeedCommentsOverlay
                    key={activeCommentsCard}
                    contributionId={activeCommentsCard}
                    currentUserId={user.id}
                    currentUser={userProfile ? {
                      first_name: userProfile.first_name,
                      last_name: userProfile.last_name,
                      username: userProfile.username,
                      avatar_url: userProfile.avatar_url
                    } : undefined}
                    ownerComment={activeItem.comment}
                    ownerUser={activeItem.user}
                    panelSide={panelSide}
                    cardRect={activeCardRect}
                    onClose={() => setActiveCommentsCard(null)}
                    onCommentAdded={() => {
                      updateContribution({ ...activeItem, comments_count: (activeItem.comments_count || 0) + 1 })
                    }}
                    onCommentDeleted={() => {
                      updateContribution({ ...activeItem, comments_count: Math.max(0, (activeItem.comments_count || 0) - 1) })
                    }}
                    syntheticThread={{
                      discussionType: 'contribution',
                      targetId: activeItem.id,
                      title: activeItem.title || activeItem.brand || 'Untitled Item',
                      slug: null,
                      imageUrl: activeItem.cached_image_url || (activeItem.images?.[0] as any)?.cached_url || null,
                      ownerId: activeItem.user_id,
                      ownerFirstName: activeItem.user?.first_name || '',
                      ownerLastName: activeItem.user?.last_name || '',
                      ownerUsername: activeItem.user?.username || null,
                      ownerAvatarUrl: activeItem.user?.avatar_url || null,
                      lastCommentContent: activeItem.comment || '',
                      lastCommentUserFirstName: activeItem.user?.first_name || '',
                      lastCommentUserLastName: activeItem.user?.last_name || '',
                      lastCommentAt: new Date().toISOString(),
                      commentCount: activeItem.comments_count || 0,
                      sharingContext: activeItem.comment || null,
                      categoryId: null, categoryName: null,
                      subcategoryId: null, subcategoryName: null,
                      userHasCommented: false,
                    }}
                  />
                )
              })()}
            </AnimatePresence>
          </>
        ) : currentView === 'brands' ? (
          // Brands view
          <div className="container mx-auto px-4">
            <BrandsDirectory />
          </div>
        ) : currentView === 'profile' ? (
          // Profile view
          <>
            <div className="container mx-auto px-4">
              <ProfileHeader
                user={userProfile || user}
                currentUserId={user.id}
                viewMode={profileViewMode}
                onViewModeChange={(mode) => setProfileViewMode(mode)}
                onProfileUpdate={() => ensureUserProfile()}
              />
            </div>
            {profileViewMode === 'issues' ? (
              <div>
                <CurationsGrid
                  curations={userCurations}
                  isOwnProfile={true}
                  username={userProfile?.username || user.email?.split('@')[0] || ''}
                  onNewCuration={() => setShowNewCurationModal(true)}
                />
              </div>
            ) : (
              <div>
                <ProfileMasonry
                  contributions={profileViewMode === 'shared' ? userContributions : savedContributions}
                  currentUserId={user.id}
                  currentUser={userProfile ? {
                    first_name: userProfile.first_name,
                    last_name: userProfile.last_name,
                    username: userProfile.username,
                    avatar_url: userProfile.avatar_url
                  } : undefined}
                  isAdmin={isAdmin}
                  onAddClick={() => navigate('/', { state: { view: 'contribute' } })}
                  isSavedSection={profileViewMode === 'saved'}
                />
              </div>
            )}
          </>
        ) : null}
      </main>

      {/* New Curation Modal */}
      {userProfile && (
        <NewCurationModal
          isOpen={showNewCurationModal}
          onClose={() => setShowNewCurationModal(false)}
          userId={user.id}
          username={userProfile.username || user.email?.split('@')[0] || ''}
          onCurationCreated={() => {
            // Reload curations after creating a new one
            fetchUserCurations(user.id)
          }}
        />
      )}

      {/* Profile Completion Modal for New Users */}
      {showProfileCompletionModal && userProfile && (
        <EditProfileModal
          isOpen={showProfileCompletionModal}
          onClose={async () => {
            // Check if profile is completed before allowing close
            const { data: latestProfile } = await supabase
              .from('users')
              .select('profile_completed')
              .eq('id', user.id)
              .single();
            
            if (latestProfile?.profile_completed) {
              setShowProfileCompletionModal(false);
              // Update local state
              setUserProfile((prev: any) => ({ ...prev, profile_completed: true }));
            }
          }}
          user={{
            ...userProfile,
            // Include Google OAuth metadata for pre-population
            user_metadata: user.user_metadata || {}
          }}
          isMandatory={true}
          onProfileUpdate={async () => {
            // Refresh user profile data after successful update
            const { data: updatedProfile } = await supabase
              .from('users')
              .select('*')
              .eq('id', user.id)
              .single();
            
            if (updatedProfile) {
              setUserProfile(updatedProfile);
              // If profile is now completed, close the modal
              if (updatedProfile.profile_completed) {
                setShowProfileCompletionModal(false);
                // Force a refresh of the contributions data to update the UI
                fetchCachedContributions(user.id, true);
              }
            }
          }}
        />
      )}
    </>
  )
}